import { useMemo } from "react";
import { GridColDef } from "@mui/x-data-grid-pro";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import {
  ColumnDimension,
  EntityCellParams,
} from "@components/common/data-grid/adm-data-grid-types";
import { getEntity } from "@components/common/data-grid/adm-data-grid-utils";
import { Subscription } from "@pages/about-products/about-products-filter-options";
import {
  ABOUT_PRODUCTS_TABLE_COLUMN_LABELS,
  AboutProductsTableColumnField,
  AboutProductsTableEntity,
  AboutProductsTableGridColDef,
  BundleFeatureChangeHandler,
} from "@pages/about-products/about-products-table-types";
import {
  FeatureIdentityCell,
  FeatureNameCell,
  NameWithDescription,
  ProductAvailabilityCell,
} from "@utils/data-grid-utils/about-products-data-grid-cells";
import { EditableAvailabilityCell } from "@utils/data-grid-utils/availability-cell";

const columnDimension: ColumnDimension<AboutProductsTableColumnField> = {
  featureName: {
    minWidth: 220,
  },
  featureIdentity: {
    minWidth: 220,
  },
};

const HEADER_WIDTH_PER_CHAR = 8;
const HEADER_HORIZONTAL_PADDING = 56;
const MIN_DYNAMIC_COLUMN_WIDTH = 120;
const MAX_DYNAMIC_COLUMN_WIDTH = 300;

/** Calculates the width of a column based on the length of its header text, with a minimum and maximum width. */
function getHeaderBasedColumnWidth(headerText: string): number {
  const calculatedWidth =
    headerText.trim().length * HEADER_WIDTH_PER_CHAR +
    HEADER_HORIZONTAL_PADDING;

  return Math.max(
    MIN_DYNAMIC_COLUMN_WIDTH,
    Math.min(MAX_DYNAMIC_COLUMN_WIDTH, calculatedWidth)
  );
}

/**
 * @param bundle the bundle rendered by the column
 * @param onChangeRequest called when the user asks to add or remove the row's feature
 * @returns a cell renderer turning every availability icon into a button
 */
function renderEditableBundleCell(
  bundle: FaroCoreApiTypes.AdmBundle,
  onChangeRequest: BundleFeatureChangeHandler
) {
  return function EditableBundleCell(
    params: EntityCellParams<AboutProductsTableEntity>
  ): React.JSX.Element {
    const entity = getEntity(params);
    const isAvailable = Boolean(entity[bundle.id]);

    return (
      <EditableAvailabilityCell
        isAvailable={isAvailable}
        onToggle={() =>
          onChangeRequest({
            bundle,
            featureId: entity.id,
            featureName: entity.featureName,
            shouldAdd: !isAvailable,
          })
        }
      />
    );
  };
}

/**
 * @param allBundles the bundles to render as dynamic availability columns
 * @param subscriptions the subscriptions to render as availability columns
 * @param onBundleFeatureChangeRequest when defined, the bundle cells become buttons that ask to
 * add or remove the row's feature. Leave it undefined to keep the bundle columns read-only.
 * @returns the columns definition to be used in the about-products table
 */
export function useAboutProductsTableColumns(
  allBundles: FaroCoreApiTypes.AdmBundle[],
  subscriptions: Subscription[],
  onBundleFeatureChangeRequest?: BundleFeatureChangeHandler
): GridColDef[] {
  return useMemo(() => {
    const staticColumns: Record<
      AboutProductsTableColumnField,
      AboutProductsTableGridColDef
    > = {
      featureName: {
        field: "featureName",
        headerName: ABOUT_PRODUCTS_TABLE_COLUMN_LABELS.featureName,
        ...columnDimension.featureName,
        align: "left",
        headerAlign: "left",
        valueGetter: (_, row) => row.entity.featureName,
        renderCell: FeatureNameCell,
      },

      featureIdentity: {
        field: "featureIdentity",
        headerName: ABOUT_PRODUCTS_TABLE_COLUMN_LABELS.featureIdentity,
        ...columnDimension.featureIdentity,
        align: "left",
        headerAlign: "left",
        valueGetter: (_, row) => row.entity.featureIdentity,
        renderCell: FeatureIdentityCell,
      },
    };

    const subscriptionColumns: GridColDef[] = subscriptions.map(
      ({ id, name, description, planType }) => ({
        field: id,
        headerName: name,
        renderHeader: () => (
          <NameWithDescription
            name={name}
            description={description}
            subtitle={planType}
          />
        ),
        width: getHeaderBasedColumnWidth(name),
        align: "center",
        headerAlign: "center",
        valueGetter: (_, row) => row.entity[id],
        renderCell: ProductAvailabilityCell,
      })
    );

    const bundleColumns: GridColDef[] = allBundles.map((bundle) => ({
      field: bundle.id,
      headerName: bundle.name,
      renderHeader: () => (
        <NameWithDescription
          name={bundle.name}
          description={bundle.description}
          subtitle={bundle.identifier}
        />
      ),
      width: getHeaderBasedColumnWidth(bundle.name),
      align: "center",
      headerAlign: "center",
      valueGetter: (_, row) => row.entity[bundle.id],
      renderCell: onBundleFeatureChangeRequest
        ? renderEditableBundleCell(bundle, onBundleFeatureChangeRequest)
        : ProductAvailabilityCell,
    }));

    return [
      ...Object.values(staticColumns),
      ...subscriptionColumns,
      ...bundleColumns,
    ];
  }, [allBundles, subscriptions, onBundleFeatureChangeRequest]);
}
